<template>
	<base-modal cancel-button-text="Cancel"
	            :confirm-button-text="t('changeDeliveryAddress.modal.confirm-button.text')"
	            :wide="true"
	            @cancel="close"
	            @confirm="confirm">

		<template v-slot:sidebar>
			<h3>
        {{ isSyngentaTenant ? t('site.syngenta.changeDeliveryAddress.modal.sidebar.title') : t('changeDeliveryAddress.modal.sidebar.title') }}
			</h3>
			<p>
				{{ t('changeDeliveryAddress.modal.sidebar.text') }}
			</p>
		</template>

		<template v-slot:title>
			{{ t('changeDeliveryAddress.modal.content.title') }}
		</template>

		<p>
			{{ t('changeDeliveryAddress.modal.content.subtitle') }}
		</p>

		<div class="input-wrapper">
			<input v-model="searchTerm" placeholder="Search addresses" type="text" />
		</div>

		<div class="modal-wrapper__addresses-container">
			<div v-for="deliveryAddress in filteredDeliveryAddresses"
			     :key="deliveryAddress.id"
			     class="input-wrapper">
				<label class="radiowrapper">
					<input v-model="localSelectedDeliveryAddressId"
					       :value="deliveryAddress.id"
					       name="adress"
					       type="radio" />

					<span class="radio-icon"></span>
					{{ deliveryAddress.deliveryAddressNameLine1 }} -
					{{ deliveryAddress.street }} -
					{{ deliveryAddress.postalCode }} -
					{{ deliveryAddress.city }} -
					{{ deliveryAddress.countryCode }}
				</label>
			</div>

			<div v-if="filteredDeliveryAddresses.length === 0">
				<span class="input-wrapper radiowrapper">No results.</span>
			</div>
		</div>

		<template v-slot:button-cancel>
			<!-- Hide default cancel button workaround -->
			<span style="display: none;"></span>
		</template>
	</base-modal>
</template>

<script lang="ts" setup>
	import BaseModal from '@/components/modals/BaseModal.vue';
	import {type DeliveryAddress} from 'dummen-shared';
	import Fuse, {type FuseResult} from 'fuse.js';
	import {computed, ref} from 'vue';
	import {useI18n} from 'vue-i18n';
	import {useCreateOrderStore} from '@/store/create.order.store.ts';

	const {t} = useI18n();

  type AvailabilityWindow = Window & {
    syngentaTenant?: boolean;
  };

  const availabilityWindow = window as AvailabilityWindow;
  const isSyngentaTenant = computed(() => availabilityWindow.syngentaTenant === true);

	const createOrderStore = useCreateOrderStore();

	const props = defineProps<{
		deliveryAddresses: DeliveryAddress[];
	}>();

	const emits = defineEmits(['close']);

	const searchTerm = ref('');

	const localSelectedDeliveryAddressId = ref(createOrderStore.selectedDeliveryAddressId);

	const filteredDeliveryAddresses = computed((): DeliveryAddress[] => {
		if (searchTerm.value.length >= 3) {
			const fuse = new Fuse(props.deliveryAddresses, {
				keys: [
					{
						name: 'deliveryAddressNameLine1',
						weight: 1
					},
					{
						name: 'street',
						weight: 0.9
					},
					{
						name: 'postalCode',
						weight: 0.8
					},
					{
						name: 'city',
						weight: 0.9
					},
					{
						name: 'countryCode',
						weight: 0.8
					}
				],
				isCaseSensitive: false,
				minMatchCharLength: 3,
				includeScore: true,
				shouldSort: true,
				ignoreLocation: true,
				threshold: 0
			});

			return fuse.search(searchTerm.value).map((address: FuseResult<DeliveryAddress>) => address.item);
		}

		return props.deliveryAddresses;
	});

	const confirm = () => {
		createOrderStore.setSelectedDeliveryAddressId(localSelectedDeliveryAddressId.value);
		close();
	}

	const close = () => {
		// Set updateAvailability false to avoid unnecessary API calls when reopening the create-order-modal.
		createOrderStore.updateAvailability = false;

		emits('close');
	};

</script>
